import { InfoIcon } from '@/assets/icons/info-icons/info';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { GET_AI_RUN_STATUS_QUERY } from '@/components/aiAssistant/queries';
import { IAiAssets } from '@/components/aiAssistant/types';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { Dispatch, SetStateAction, useState } from 'react';
import styled from 'styled-components';
import { DELETE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION } from '../../../dealPipeline-page/queries';
import { STRATEGY_DETAILS_PAGE_UPDATE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION } from '../../tabs/updatesTab/queries';

interface DeleteAttachmentProps {
  isOpen: boolean;
  currentAttachment: IOfferingMaterialsAttachments | null;
  recordId: string | null;
  notVisibleTitle: string;
  triggerAiRunStatusRefetch: () => void;
  documentRecordRefetch: () => void;
  setUploadedFiles: Dispatch<SetStateAction<IOfferingMaterialsAttachments[] | []>>;
  setFilesForAiAssistant: Dispatch<SetStateAction<IAiAssets[]>>;
  onClose: () => void;
}

const DeleteAttachment = ({
  isOpen,
  currentAttachment,
  recordId,
  notVisibleTitle,
  triggerAiRunStatusRefetch,
  documentRecordRefetch,
  setUploadedFiles,
  setFilesForAiAssistant,
  onClose
}: DeleteAttachmentProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAttachmentUseForAiAssistant, setIsAttachmentUseForAiAssistant] = useState(false);

  const [deleteAttachment, { loading: deleteAttachmentLoading }] = useMutation(DELETE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION);
  const [updateAttachment, { loading: updateAttachmentLoading }] = useMutation(
    STRATEGY_DETAILS_PAGE_UPDATE_INVESTMENT_OFFERING_MATERIALS_ATTACHMENT_MUTATION
  );

  const { loading: getAIRunStatusLoading } = useQuery(GET_AI_RUN_STATUS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !recordId,
    variables: {
      recordId: recordId,
      documentType: 'sd'
    },
    onCompleted: (data) => {
      setIsAttachmentUseForAiAssistant(data?.getAIRunStatus?.filenames?.includes(currentAttachment?.asset?.assetKey?.split('/')?.pop()));
    }
  });

  const deleteFile = () => {
    setErrorMessage(null);

    if (!currentAttachment) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    deleteAttachment({
      variables: {
        id: currentAttachment.id
      },
      onCompleted: () => {
        if (isAttachmentUseForAiAssistant) {
          documentRecordRefetch();
          triggerAiRunStatusRefetch();
        }
        setUploadedFiles((prev) => prev.filter((file) => file.id !== currentAttachment.id));
        setFilesForAiAssistant((prev) => prev.filter((file) => file.filename !== currentAttachment?.asset?.assetKey?.split('/')?.pop()));
        onClose();
      },
      onError: (error) => {
        console.error(error);
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  const makeFilePrivate = () => {
    if (currentAttachment.visibility === notVisibleTitle) {
      onClose();
      return;
    }

    updateAttachment({
      variables: {
        data: {
          id: currentAttachment.id,
          visibility: notVisibleTitle
        }
      },
      onCompleted: () => {
        setUploadedFiles((prev) =>
          prev.map((file) => {
            if (file.id === currentAttachment.id) {
              return { ...file, visibility: notVisibleTitle };
            }
            return file;
          })
        );
        onClose();
      },
      onError: (error) => {
        console.error(error);
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  if (getAIRunStatusLoading) return <></>;

  if (isAttachmentUseForAiAssistant) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={onClose}
        showCloseIcon={!deleteAttachmentLoading || !updateAttachmentLoading}
        isUseOutsideClick={!deleteAttachmentLoading || !updateAttachmentLoading}
        showCancelButton={false}
      >
        <ModalBody>
          <DeleteBucketIcon fill={themeVar('font.disabled')} />
          {deleteAttachmentLoading || updateAttachmentLoading ? (
            <ModalLoader />
          ) : (
            <>
              <ModalTitle>This attachment is in use by the AI Assistant.</ModalTitle>
              {errorMessage && (
                <ErrorBannerWrapper>
                  <Banner icon={<InfoIcon width={26} height={26} />} title="Error" description={errorMessage} bgColor={themeVar('context.error')} />
                </ErrorBannerWrapper>
              )}
              <ModalDescription>
                You can <span style={{ fontWeight: 700 }}>clear the AI answers</span> and delete the attachment, or mark the attachment as "
                {notVisibleTitle}" to keep it available for the AI.
              </ModalDescription>
            </>
          )}
          <Actions>
            {!deleteAttachmentLoading && !updateAttachmentLoading && (
              <Button styleType="outline" onClick={onClose} size="md">
                Cancel
              </Button>
            )}
            <Button onClick={makeFilePrivate} styleType={deleteAttachmentLoading || updateAttachmentLoading ? 'disabled' : 'default'} size="md">
              Make File Private
            </Button>
            <Button onClick={deleteFile} styleType={deleteAttachmentLoading || updateAttachmentLoading ? 'disabled' : 'default'} size="md">
              Delete File
            </Button>
          </Actions>
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Delete File',
        onClick: deleteFile,
        disabled: deleteAttachmentLoading
      }}
      showCloseIcon={!deleteAttachmentLoading}
      showCancelButton={!deleteAttachmentLoading}
      isUseOutsideClick={!deleteAttachmentLoading}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {deleteAttachmentLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this attachment.</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner icon={<InfoIcon width={26} height={26} />} title="Error" description={errorMessage} bgColor={themeVar('context.error')} />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>There is no undo for this operation.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteAttachment;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  column-gap: 10px;
  padding: 30px 0 15px;
`;
